import PatPoints from "../../football/enums/pat-points";
import FirstDownStyles from "../../football/enums/first-down-styles";
import ClockType from "../../football/enums/clock-type";
import FieldLength from "../../football/enums/field-length";
import FieldAreaType from "../../football/enums/field-area-type";
import ParticipationStyle from "../../football/enums/participation-style";

const DefaultFlagFootballConfig = Object.freeze({
    general: Object.freeze({
        periodsInRegulation: 4,
        downs: 4,
        fieldLength: FieldLength.OptionFive.code,
        fieldWidth: 40,
        minutesByPeriod: 12,
        // PSTATS-1268, see football/config/default.js: whether overtime is
        // timed, and for how long. Off, so flag overtime stays untimed.
        otTimeControl: false,
        otMinutesByPeriod: 10,
        firstDownYards: 10, //traditional
        firstDownYardSet: [20, 40, 60, 80], //flag
        // When true, the crew has hand-edited firstDownYardSet, so startGame()
        // must preserve it instead of recomputing uniform zones from fieldLength.
        customFirstDownYardSet: false,
        firstDownFlagStyle: FirstDownStyles.ZoneToGain.code,
        timeoutsPerHalf: 3,
        // PSTATS-1268, see football/config/default.js. USA Flag grants no
        // timeouts in overtime, so timeouts stay unavailable there unless a
        // season sets a number.
        otTimeouts: 0,
        redZoneLength: 10,
        zoneLength: 20,
        endZoneLength: 10,
        rushType: false,
        passType: false,
        spikeTeamAttOnly: false,
        touchdownsCountAsFirstDowns: false,
        // Opt-in per season or event. Off by default so existing records are
        // unaffected: season and event config spread the defaults first and
        // saved values over the top, so a config saved before this key existed
        // simply inherits false - no migration needed.
        fumbledSnapEnabled: false,
        // Opt-out per season or event. On by default, and configs saved
        // before this key existed lack it entirely, so readers must treat
        // only an explicit false as off.
        sackFlowEnabled: true,
        // Opt-out on the same terms as sackFlowEnabled. Gates every Fumble
        // entry point; Fumble is the only way into the recovered / lost
        // subtree, so the one gate closes the whole flow. Independent of
        // fumbledSnapEnabled: a bad snap with fumbles off continues as an
        // ordinary play.
        fumbleFlowEnabled: true,
        // Opt-in per season or event. The general tournament rules make an
        // intercepted try a failed attempt; USA Flag / 2023 Flag Rules award
        // the defense 2 points for a touchdown on a try. Off by default so
        // configs saved before this key existed keep today's behaviour.
        defensivePatReturnEnabled: false,
        // Opt-in per season or event. Off by default so existing records are
        // unaffected: season and event config spread the defaults first and
        // saved values over the top, so a config saved before this key
        // existed simply inherits false - no migration needed. When on, the
        // crew records kickoffs (throw-offs) at the start of each half and
        // after a touchdown plus PAT.
        kickoffEnabled: false,
        teamLossOnly: [],
        teamZoneQty: 2,
        // Persisted field regions (yard coordinates within 0..fieldLength),
        // configured per event via the Field Areas marker editor. Only redZone
        // entries affect stats (via redZoneLength); the rest are display
        // metadata. Empty by default so it stays valid at any field length; the
        // editor seeds a layout (see UI). See [[field-config-markers]].
        fieldAreaSet: [
            { start: 0, end: 10, type: FieldAreaType.RedZone.code },
            { start: 70, end: 80, type: FieldAreaType.RedZone.code }
        ],
        participationStyle: ParticipationStyle.Default.code
    }),
    clock: Object.freeze({
        clockType: ClockType.DESC.code,
        runningClock: false
    }),
    spots: Object.freeze({
        kickoff: 35,
        driveStartingYardline: 14,
        touchbackYardline: 14,
        // PSTATS-1268, see football/config/default.js. In flag football it
        // acts only while general.kickoffEnabled is on (isOtKickoffsOn).
        otKickoffs: false,
        overtimeStartingYardline: 10,
        safetyYardline: 14,
        spotsPatScoreOption1: 3,
        spotsPatScoreOption2: 10,
        spotsPatScoreOption3: 20
    }),
    scoring: Object.freeze({
        touchdown: 6,
        safety: 2,
        rougePoints: 1,
        fieldGoal: 3,
        defensivePAT: 2,
        scoringPatScoreOption1: PatPoints.OptionOne.value,
        scoringPatScoreOption2: PatPoints.OptionTwo.value,
        scoringPatScoreOption3: PatPoints.OptionThree.value,
        playSummary: true
    })
});

export default DefaultFlagFootballConfig;
